import React, { useEffect, useMemo, useState } from 'react'
import ContainerDashboard from '../../components/layouts/ContainerDashboard'
import Loader from '../Loader'
import swal from 'sweetalert'
import moment from 'moment'
import { DatePicker } from 'antd'
import { axiosInstance } from '../Config'
import Pagination from '../../components/Pagination'

const { RangePicker } = DatePicker

const avatarColors = ['#0CACC2', '#A44CD7', '#F55145', '#F9BE22', '#8A3BBD', '#19C37D']
const getInitials = (name = '') => name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
const labelStyle = { fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#999' }

const statusStyle = (status = '') => {
    switch (String(status).toLowerCase()) {
        case 'paid':
        case 'success':
        case 'captured':
            return { bg: '#e8f8f0', color: '#1a9e5c', dot: '#1a9e5c' }
        case 'pending':
        case 'created':
            return { bg: '#fff7e6', color: '#d48806', dot: '#d48806' }
        case 'failed':
        case 'cancelled':
            return { bg: '#fff0f0', color: '#e03131', dot: '#e03131' }
        default:
            return { bg: '#f0f0ff', color: '#7c3aed', dot: '#7c3aed' }
    }
}

const fmtAmount = (amount, currency = 'INR') => {
    const symbol = currency === 'INR' ? '₹' : `${currency} `
    return `${symbol}${Number(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`
}

const PaymentsIndex = () => {
    const [payments, setPayments] = useState([])
    const [loading, setLoading] = useState(false)
    const [searchQuery, setSearchQuery] = useState('')
    const [status, setStatus] = useState('All')
    const [range, setRange] = useState(null)
    const [dateValue, setDateValue] = useState(null)
    const [page, setPage] = useState(1)
    const [itemsPerPage, setItemsPerPage] = useState(10)
    const [selected, setSelected] = useState(null)

    const hasActiveFilters = searchQuery.trim() !== '' || status !== 'All' || range !== null || itemsPerPage !== 10

    const handleReset = () => {
        setSearchQuery('')
        setStatus('All')
        setRange(null)
        setDateValue(null)
        setItemsPerPage(10)
        setPage(1)
    }

    useEffect(() => {
        document.title = 'Payments | Mindora'
        window.scrollTo(0, 0)
        fetchPayments()
    }, [])

    const fetchPayments = async () => {
        setLoading(true)
        try {
            const res = await axiosInstance.get('payment')
            setPayments(res.data?.payments ?? res.data?.data ?? [])
        } catch (err) {
            console.error(err)
            swal('', 'Failed to load payments.', 'error')
        } finally {
            setLoading(false)
        }
    }

    const filtered = useMemo(() => {
        const q = searchQuery.trim().toLowerCase()
        return payments.filter(p => {
            if (status !== 'All' && String(p.status || '').toLowerCase() !== status.toLowerCase()) return false
            if (range?.[0] && range?.[1]) {
                const d = moment(p.createdAt)
                if (!d.isBetween(moment(range[0]).startOf('day'), moment(range[1]).endOf('day'), null, '[]')) return false
            }
            if (!q) return true
            const planName = p.kidPlan?.name || p.tutorPlan?.name || ''
            return [
                p.parent?.name, p.parent?.email,
                p.razorpayOrderId, p.razorpayPaymentId,
                planName, String(p.id), String(p.amount),
            ].filter(Boolean).some(v => String(v).toLowerCase().includes(q))
        })
    }, [payments, searchQuery, status, range])

    const totalPages = Math.max(1, Math.ceil(filtered.length / itemsPerPage))
    const safePage = Math.min(page, totalPages)
    const paged = filtered.slice((safePage - 1) * itemsPerPage, safePage * itemsPerPage)

    const stats = useMemo(() => {
        const paid = payments.filter(p => String(p.status).toLowerCase() === 'paid')
        const pending = payments.filter(p => String(p.status).toLowerCase() === 'pending')
        const revenue = paid.reduce((sum, p) => sum + Number(p.amount || 0), 0)
        return { total: payments.length, paid: paid.length, pending: pending.length, revenue }
    }, [payments])

    const planOf = (p) => p.kidPlan ?? p.tutorPlan ?? null
    const planTypeOf = (p) => {
        if (p.kidPlanId && p.kidPlan) return 'Kid'
        if (p.tutorPlanId && p.tutorPlan) return 'Tutor'
        if (p.kidPlan) return 'Kid'
        if (p.tutorPlan) return 'Tutor'
        return '—'
    }

    return (
        <>
            {loading && <Loader />}
            <ContainerDashboard title="Payments" description="All Razorpay orders with parent and plan details">
                <section className="ps-dashboards p-4">

                    {/* Stats Row */}
                    <div className="row g-3 mb-4">
                        {[
                            { label: 'Total Payments', value: stats.total, icon: 'bi-credit-card', color: '#0CACC2', bg: 'rgba(12,172,194,0.12)' },
                            { label: 'Paid', value: stats.paid, icon: 'bi-check-circle', color: '#19C37D', bg: 'rgba(25,195,125,0.12)' },
                            { label: 'Pending', value: stats.pending, icon: 'bi-hourglass-split', color: '#F9BE22', bg: 'rgba(249,190,34,0.12)' },
                            { label: 'Total Revenue', value: fmtAmount(stats.revenue), icon: 'bi-cash-stack', color: '#A44CD7', bg: 'rgba(164,76,215,0.12)' },
                        ].map(s => (
                            <div className="col-md-3" key={s.label}>
                                <div className="rounded-4 p-3 d-flex align-items-center gap-3"
                                    style={{ background: '#fff', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
                                    <div className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
                                        style={{ width: 48, height: 48, background: s.bg }}>
                                        <i className={`bi ${s.icon}`} style={{ fontSize: 22, color: s.color }}></i>
                                    </div>
                                    <div>
                                        <p className="mb-0" style={labelStyle}>{s.label}</p>
                                        <h4 className="mb-0 fw-bold" style={{ color: '#1a1a2e' }}>{s.value}</h4>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Section heading */}
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                        <h6 className="fw-bold mb-0" style={{ color: '#1a1a2e', fontSize: 16 }}>
                            All Payments
                            <span className="ms-2 badge rounded-pill"
                                style={{ background: 'rgba(12,172,194,0.12)', color: '#0CACC2', fontSize: 12 }}>
                                {filtered.length}
                            </span>
                            {searchQuery.trim() && (
                                <span className="ms-2 text-muted fw-normal" style={{ fontSize: 13 }}>
                                    — results for &quot;{searchQuery.trim()}&quot;
                                </span>
                            )}
                        </h6>
                        <button type="button" className="btn btn-sm fw-semibold"
                            style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '8px 14px', fontSize: 13, color: '#1a1a2e', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}
                            onClick={fetchPayments}>
                            <i className="bi bi-arrow-clockwise me-1"></i>Refresh
                        </button>
                    </div>

                    {/* Filters */}
                    <div className="rounded-4 p-3 p-md-4 mb-4" style={{ background: '#fff', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
                        <style>{`
                            .payments-filters .form-label { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.6px; color: #999; margin-bottom: 6px; display: block; }
                            .payments-filters .form-control, .payments-filters .form-select { height: 44px; border-radius: 10px; font-size: 14px; border: 1px solid #e5e7eb; }
                            .payments-filters .form-control:focus, .payments-filters .form-select:focus { border-color: #0CACC2; box-shadow: 0 0 0 3px rgba(12,172,194,0.12); outline: none; }
                            .payments-filters .ps-form--search-simple { max-width: 100%; }
                            .payments-filters .ps-form--search-simple input { width: 100%; height: 44px; border-radius: 10px; font-size: 14px; border: 1px solid #e5e7eb; padding-right: 40px; }
                            .payments-filters .ant-picker { height: 44px; border-radius: 10px; width: 100%; border: 1px solid #e5e7eb; }
                            .payments-filters .ant-picker:hover, .payments-filters .ant-picker-focused { border-color: #0CACC2; box-shadow: 0 0 0 3px rgba(12,172,194,0.12); }
                        `}</style>
                        <div className="payments-filters d-flex flex-wrap align-items-end" style={{ gap: 12 }}>
                            <div style={{ flex: '1 1 240px', minWidth: 220 }}>
                                <label className="form-label" htmlFor="payment-search">Search</label>
                                <div className="ps-form--search-simple" style={{ maxWidth: '100%' }}>
                                    <input
                                        id="payment-search"
                                        className="form-control bg-white"
                                        type="text"
                                        value={searchQuery}
                                        onChange={e => { setSearchQuery(e.target.value); setPage(1) }}
                                        placeholder="Search parent, email, order id..."
                                    />
                                    <button type="button" tabIndex={-1}>
                                        <i className="icon icon-magnifier"></i>
                                    </button>
                                </div>
                            </div>
                            <div style={{ flex: '0 1 160px', minWidth: 150 }}>
                                <label className="form-label" htmlFor="payment-status">Status</label>
                                <select id="payment-status" className="form-select" value={status}
                                    onChange={e => { setStatus(e.target.value); setPage(1) }}>
                                    <option value="All">All Status</option>
                                    <option value="paid">Paid</option>
                                    <option value="pending">Pending</option>
                                    <option value="failed">Failed</option>
                                </select>
                            </div>
                            <div style={{ flex: '0 1 120px', minWidth: 110 }}>
                                <label className="form-label" htmlFor="payment-perpage">Show</label>
                                <select id="payment-perpage" className="form-select" value={itemsPerPage}
                                    onChange={e => { setItemsPerPage(Number(e.target.value)); setPage(1) }}>
                                    <option value={5}>5 / page</option>
                                    <option value={10}>10 / page</option>
                                    <option value={20}>20 / page</option>
                                    <option value={30}>30 / page</option>
                                </select>
                            </div>
                            <div style={{ flex: '1 1 240px', minWidth: 230, maxWidth: 320 }}>
                                <label className="form-label">Date range</label>
                                <RangePicker
                                    value={dateValue}
                                    onChange={(dates, dateString) => {
                                        setPage(1)
                                        setDateValue(dates)
                                        setRange(dateString?.[0] && dateString?.[1] ? dateString : null)
                                    }}
                                    allowClear
                                />
                            </div>
                            <div style={{ flex: '0 0 auto' }}>
                                <button type="button" className="btn fw-semibold"
                                    style={{
                                        height: 44, borderRadius: 10, padding: '0 18px', fontSize: 13,
                                        background: hasActiveFilters ? '#fff0f0' : '#f4f5f7',
                                        color: hasActiveFilters ? '#e53935' : '#999',
                                        border: hasActiveFilters ? '1px solid #ffd0d0' : '1px solid #e5e7eb',
                                        cursor: hasActiveFilters ? 'pointer' : 'not-allowed',
                                        whiteSpace: 'nowrap',
                                    }}
                                    disabled={!hasActiveFilters}
                                    onClick={handleReset}
                                    title="Clear all filters">
                                    <i className="bi bi-x-circle me-1"></i>Reset
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="table-responsive" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', borderRadius: 12 }}>
                        <table className="jean-table table" style={{ minWidth: 900 }}>
                            <tbody>
                                {paged.map((p, index) => {
                                    const st = statusStyle(p.status)
                                    const plan = planOf(p)
                                    return (
                                        <tr key={p.id}>
                                            <td style={{ width: 60 }}>
                                                <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                                                    style={{ width: 46, height: 46, background: avatarColors[index % avatarColors.length], color: '#fff', fontWeight: 700, fontSize: 15 }}>
                                                    {getInitials(p.parent?.name || 'P')}
                                                </div>
                                            </td>
                                            <td>
                                                <p style={labelStyle} className="mb-1">Parent</p>
                                                <p className="mb-0 fw-semibold" style={{ color: '#1a1a2e' }}>{p.parent?.name || '—'}</p>
                                                <p className="mb-0" style={{ fontSize: 12, color: '#999' }}>
                                                    <i className="bi bi-envelope me-1"></i>{p.parent?.email || '—'}
                                                </p>
                                            </td>
                                            <td>
                                                <p style={labelStyle} className="mb-1">Plan</p>
                                                <p className="mb-0 fw-semibold" style={{ color: '#1a1a2e' }}>{plan?.name || '—'}</p>
                                                <p className="mb-0" style={{ fontSize: 12, color: '#999' }}>{planTypeOf(p)} Plan</p>
                                            </td>
                                            <td>
                                                <p style={labelStyle} className="mb-1">Amount</p>
                                                <p className="mb-0 fw-bold" style={{ color: '#1a1a2e' }}>{fmtAmount(p.amount, p.currency)}</p>
                                                <p className="mb-0" style={{ fontSize: 12, color: '#999' }}>{p.currency || ''}</p>
                                            </td>
                                            <td>
                                                <p style={labelStyle} className="mb-1">Status</p>
                                                <span className="d-inline-flex align-items-center gap-1 px-2 py-1 rounded-pill fw-semibold"
                                                    style={{ background: st.bg, color: st.color, fontSize: 12, whiteSpace: 'nowrap', textTransform: 'capitalize' }}>
                                                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: st.dot, display: 'inline-block', flexShrink: 0 }}></span>
                                                    {p.status}
                                                </span>
                                            </td>
                                            <td>
                                                <p style={labelStyle} className="mb-1">Order Id</p>
                                                <p className="mb-0" style={{ fontSize: 12, color: '#1a1a2e', fontFamily: 'monospace' }}>{p.razorpayOrderId || '—'}</p>
                                                <p className="mb-0" style={{ fontSize: 11, color: '#999', fontFamily: 'monospace' }}>{p.razorpayPaymentId || 'No payment id'}</p>
                                            </td>
                                            <td>
                                                <p style={labelStyle} className="mb-1">Date</p>
                                                <p className="mb-0 fw-semibold" style={{ color: '#1a1a2e', whiteSpace: 'nowrap', fontSize: 13 }}>
                                                    {p.createdAt ? moment(p.createdAt).format('DD MMM, YYYY') : '—'}
                                                </p>
                                                <p className="mb-0" style={{ fontSize: 11, color: '#999' }}>
                                                    {p.createdAt ? moment(p.createdAt).format('hh:mm A') : ''}
                                                </p>
                                            </td>
                                            <td className="pe-3">
                                                <div className="d-flex justify-content-end">
                                                    <button type="button" className="btn fw-semibold"
                                                        style={{ background: 'linear-gradient(135deg, #0CACC2, #A44CD7)', color: '#fff', borderRadius: 8, padding: '6px 16px', fontSize: 13, border: 'none', whiteSpace: 'nowrap' }}
                                                        onClick={() => setSelected(p)}>
                                                        <i className="bi bi-eye me-1"></i>View More
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    )
                                })}
                                {paged.length === 0 && !loading && (
                                    <tr>
                                        <td colSpan={8}>
                                            <div className="text-center py-5" style={{ color: '#aaa' }}>
                                                <i className="bi bi-credit-card" style={{ fontSize: 52, opacity: 0.3 }}></i>
                                                <p className="mt-3 mb-0">No payments found.</p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    <Pagination
                        page={safePage} totalPages={totalPages}
                        setPage={setPage} totalItems={filtered.length}
                        showingCount={paged.length}
                    />

                </section>
            </ContainerDashboard>

            {selected && (
                <PaymentDetailModal payment={selected} onClose={() => setSelected(null)} />
            )}
        </>
    )
}

const PaymentDetailModal = ({ payment: p, onClose }) => {
    const st = statusStyle(p.status)
    const plan = p.kidPlan ?? p.tutorPlan ?? null
    const planType = p.kidPlanId ? 'Kid' : p.tutorPlanId ? 'Tutor' : '—'

    const rows = [
        { label: 'Payment ID', value: `#${p.id}`, icon: 'bi-hash', color: '#0CACC2' },
        { label: 'Amount', value: fmtAmount(p.amount, p.currency), icon: 'bi-cash', color: '#19C37D' },
        { label: 'Currency', value: p.currency || '—', icon: 'bi-globe', color: '#A44CD7' },
        { label: 'Order ID', value: p.razorpayOrderId || '—', icon: 'bi-receipt', color: '#F55145', mono: true },
        { label: 'Razorpay Payment ID', value: p.razorpayPaymentId || 'Not paid yet', icon: 'bi-credit-card', color: '#F9BE22', mono: true },
        { label: 'Plan', value: plan ? `${plan.name} (${planType})` : '—', icon: 'bi-star', color: '#8A3BBD' },
        { label: 'Parent', value: p.parent ? `${p.parent.name} · ${p.parent.email}` : '—', icon: 'bi-person', color: '#0CACC2' },
        { label: 'Created', value: p.createdAt ? moment(p.createdAt).format('DD MMM YYYY, hh:mm A') : '—', icon: 'bi-calendar3', color: '#F55145' },
        { label: 'Updated', value: p.updatedAt ? moment(p.updatedAt).format('DD MMM YYYY, hh:mm A') : '—', icon: 'bi-clock', color: '#5A79DF' },
    ]

    return (
        <div
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.52)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
            onClick={e => { if (e.target === e.currentTarget) onClose() }}
        >
            <style>{`
                @keyframes paymentDetailIn {
                    from { transform: translateY(-24px) scale(0.96); opacity: 0; }
                    to   { transform: translateY(0) scale(1); opacity: 1; }
                }
            `}</style>

            <div style={{ width: '100%', maxWidth: 620, animation: 'paymentDetailIn 0.25s ease', maxHeight: '90vh', overflowY: 'auto', borderRadius: 16 }}>
                <div className="rounded-4 overflow-hidden" style={{ background: '#fff', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>

                    <div className="position-relative overflow-hidden px-4 py-4"
                        style={{ background: 'linear-gradient(135deg, #0CACC2 0%, #7B4FBF 50%, #A44CD7 100%)' }}>
                        <div className="d-flex align-items-center gap-3 position-relative">
                            <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                                style={{ width: 52, height: 52, background: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 700, fontSize: 18, border: '2px solid rgba(255,255,255,0.3)' }}>
                                {getInitials(p.parent?.name || 'P')}
                            </div>
                            <div className="flex-grow-1">
                                <p className="fw-bold text-white mb-0" style={{ fontSize: 17 }}>
                                    {fmtAmount(p.amount, p.currency)}
                                    <span className="ms-2 px-2 py-1 rounded-pill fw-semibold"
                                        style={{ background: 'rgba(255,255,255,0.2)', fontSize: 11, textTransform: 'capitalize' }}>
                                        {p.status}
                                    </span>
                                </p>
                                <p className="mb-0" style={{ fontSize: 13, color: 'rgba(255,255,255,0.85)', fontFamily: 'monospace' }}>
                                    {p.razorpayOrderId}
                                </p>
                            </div>
                            <button onClick={onClose}
                                style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '50%', width: 34, height: 34, color: '#fff', fontSize: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                ✕
                            </button>
                        </div>
                    </div>

                    <div className="p-4">
                        <div className="d-flex flex-column gap-2">
                            {rows.map((r, i) => (
                                <div key={i} className="d-flex align-items-center gap-3 p-3 rounded-3" style={{ background: '#fafbfc' }}>
                                    <div className="rounded-2 d-flex align-items-center justify-content-center flex-shrink-0"
                                        style={{ width: 36, height: 36, background: `${r.color}15` }}>
                                        <i className={`bi ${r.icon}`} style={{ fontSize: 15, color: r.color }}></i>
                                    </div>
                                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                        <p className="mb-0" style={{ fontSize: 10, fontWeight: 600, color: '#999', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                                            {r.label}
                                        </p>
                                        <p className="mb-0 fw-semibold" style={{ fontSize: 13, color: '#1a1a2e', wordBreak: 'break-all', fontFamily: r.mono ? 'monospace' : 'inherit' }}>
                                            {r.value}
                                        </p>
                                    </div>
                                </div>
                            ))}
                            <div className="d-flex align-items-center gap-3 p-3 rounded-3" style={{ background: st.bg }}>
                                <span style={{ width: 10, height: 10, borderRadius: '50%', background: st.dot, display: 'inline-block' }}></span>
                                <p className="mb-0 fw-semibold" style={{ fontSize: 13, color: st.color, textTransform: 'capitalize' }}>
                                    Status: {p.status}
                                </p>
                            </div>
                            {plan?.description && (
                                <div className="p-3 rounded-3" style={{ background: '#fff', border: '1px solid #f0f0f0' }}>
                                    <p className="mb-1" style={{ fontSize: 10, fontWeight: 600, color: '#999', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                                        Plan Description
                                    </p>
                                    <p className="mb-0" style={{ fontSize: 13, color: '#444' }}>{plan.description}</p>
                                </div>
                            )}
                        </div>
                    </div>

                </div>
            </div>
        </div>
    )
}

export default PaymentsIndex
